import { getAllESQueryFull, runESQueryFull } from "@knowt/syncing/fetchFunctions/elasticsearch";
import { FlashcardSet, Folder, Note, UserDetails } from "@knowt/syncing/graphql/schema";
import { cache } from "react";
import { USER_PUBLIC_PICKED_FIELDS } from "@knowt/syncing/hooks/user/graphqlUtils";

export type ProfilePageSearchParams = {
    page?: number;
    nextToken?: string;
};

/**
 * - get all items that is not inside a folder
 * - get all items that is inside a folder, only if the folder is private
 */
export const profilePageFilterItems = (items: (Note | FlashcardSet)[], folders: Folder[]) => {
    return items.filter(item => {
        const itemFolderId = item.folderId;

        if (!itemFolderId) {
            return true;
        } else {
            if (!folders.find(folder => itemFolderId === folder.folderId)) {
                return true;
            }
        }

        return false;
    });
};

export const getUserProfileInfo = cache(async (username: string) => {
    const userQuery = await runESQueryFull({
        queryFields: ["username.keyword"],
        queryPhrase: username.toLowerCase(),
        returnFields: USER_PUBLIC_PICKED_FIELDS,
        searchIndex: ["USER"],
        pagesize: 30,
        exactMatch: true,
    });

    if (!userQuery.total) {
        return null;
    }

    // * This is needed because the elasticsearch can return multiple users with the same username starting with the same prefix
    // * https://www.elastic.co/guide/en/elasticsearch/reference/current/query-dsl-match-query.html
    // * for match queries, there is no such thing as exact match, since it tokenizes the query and matches on the tokens
    const userInfo = (userQuery?.items as UserDetails[])?.filter(
        (u: UserDetails) => u.username === username.toLowerCase()
    )?.[0];

    return userInfo;
});

type ItemFetcherProps = { userId: string } & ProfilePageSearchParams;
export const getUserProfileNotes = async ({
    userId,
    page: _page,
    nextToken,
}: ItemFetcherProps): Promise<{ notes: Note[]; total: number }> => {
    const page = Number(_page) || 0;

    // TODO(refactor): use getNotes from esFetchers
    const notesQuery = await getAllESQueryFull<Note>({
        queryFields: ["userId"],
        queryPhrase: userId,
        returnFields: [
            "noteId",
            "userId",
            "schoolId",
            "folderId",
            "tags",
            "grade",
            "title",
            "subject",
            "updated",
            "rating",
            "ratingCount",
            "public",
            "topic",
            "verified",
            "icon",
            "rating",
            "ratingCount",
            "created",
            "trash",
            "views",
        ],
        searchIndex: ["NOTE"],
        filters: { trash: false, public: true },
        page,
        pagesize: 50,
        nextToken,
        timeKey: "updated",
    });

    return {
        notes: notesQuery?.items || [],
        total: notesQuery?.total || 0,
    };
};

export const getUserProfileFlashcardSets = async ({
    userId,
    page: _page,
    nextToken,
}: ItemFetcherProps): Promise<{ flashcardSets: FlashcardSet[]; total: number }> => {
    const page = Number(_page) || 0;

    // TODO(refactor): use getFlashcardSets from esFetchers
    const flashcardSetsQuery = await getAllESQueryFull<FlashcardSet>({
        queryFields: ["userId"],
        queryPhrase: userId,
        returnFields: [
            "flashcardSetId",
            "userId",
            "title",
            "public",
            "size",
            "folderId",
            "subject",
            "updated",
            "schoolId",
            "grade",
            "tags",
            "topic",
            "icon",
            "rating",
            "ratingCount",
            "draft",
            "created",
            "trash",
            "noteId",
            "views",
        ],
        filters: { trash: false, public: true, draft: false },
        searchIndex: ["FLASHCARDSET"],
        page,
        pagesize: 50,
        nextToken,
        timeKey: "updated",
    });

    return {
        flashcardSets: flashcardSetsQuery?.items || [],
        total: flashcardSetsQuery?.total || 0,
    };
};
